import { RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import { useDashboardMetrics } from "@/hooks/useDashboardMetrics";

type Accent = "neutral" | "green" | "amber" | "muted" | "red";

const accentBar: Record<Accent, string> = {
  neutral: "bg-slate-500",
  green: "bg-emerald-500",
  amber: "bg-amber-500",
  muted: "bg-slate-600",
  red: "bg-red-500",
};

const accentNumber: Record<Accent, string> = {
  neutral: "text-white",
  green: "text-emerald-400",
  amber: "text-amber-400",
  muted: "text-slate-400",
  red: "text-red-400",
};

interface StatCardProps {
  label: string;
  value: number | undefined;
  accent: Accent;
  loading: boolean;
  error: boolean;
  hint?: { text: string; tone: "good" | "warn" | "bad" };
  to?: string;
}

const hintTone: Record<"good" | "warn" | "bad", string> = {
  good: "text-emerald-400",
  warn: "text-amber-400",
  bad: "text-red-400",
};

const StatCard = ({ label, value, accent, loading, error, hint, to }: StatCardProps) => {
  const navigate = useNavigate();
  const clickable = !!to;
  return (
    <div
      onClick={clickable ? () => navigate(to!) : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(to!);
              }
            }
          : undefined
      }
      className={`relative bg-slate-800 border border-slate-700 rounded-md p-4 overflow-hidden transition-all ${
        clickable
          ? "cursor-pointer hover:ring-1 hover:ring-slate-600 focus:outline-none focus:ring-1 focus:ring-slate-500"
          : ""
      }`}
    >
      <span
        className={`absolute left-0 top-0 bottom-0 w-1 ${accentBar[accent]}`}
        aria-hidden
      />
      <div className="pl-2">
        <div className="text-xs text-slate-400 uppercase tracking-wide">{label}</div>
        <div className="mt-2 h-9 flex items-end">
          {loading ? (
            <div className="h-7 w-16 rounded bg-slate-700 animate-pulse" />
          ) : error ? (
            <span
              className="text-3xl font-bold text-red-400"
              title="Data unavailable"
            >
              —
            </span>
          ) : (
            <span className={`text-3xl font-bold ${accentNumber[accent]}`}>
              {value ?? 0}
            </span>
          )}
        </div>
        {hint && !loading && !error && (
          <div className={`mt-1 text-xs ${hintTone[hint.tone]}`}>{hint.text}</div>
        )}
      </div>
    </div>
  );
};

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
    {children}
  </h3>
);

export default function Dashboard() {
  const { metrics, isLoading, error, lastUpdated, refetch, isFetching } =
    useDashboardMetrics();

  const hasError = !!error;

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-8 max-w-6xl">
        {/* Companies */}
        <section>
          <SectionLabel>Companies</SectionLabel>
          <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <StatCard
              label="Total Companies"
              value={metrics?.totalCompanies}
              accent="neutral"
              loading={isLoading}
              error={hasError}
              to="/companies"
            />
            <StatCard
              label="Active"
              value={metrics?.activeCompanies}
              accent="green"
              loading={isLoading}
              error={hasError}
              to="/companies"
            />
            <StatCard
              label="Suspended"
              value={metrics?.suspendedCompanies}
              accent="amber"
              loading={isLoading}
              error={hasError}
              to="/companies"
            />
          </div>
        </section>

        {/* Users */}
        <section>
          <SectionLabel>Users</SectionLabel>
          <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <StatCard
              label="Total Users"
              value={metrics?.totalUsers}
              accent="neutral"
              loading={isLoading}
              error={hasError}
              to="/users"
            />
            <StatCard
              label="Active"
              value={metrics?.activeUsers}
              accent="green"
              loading={isLoading}
              error={hasError}
              to="/users"
            />
            <StatCard
              label="Suspended"
              value={metrics?.suspendedUsers}
              accent="amber"
              loading={isLoading}
              error={hasError}
              to="/users"
            />
            <StatCard
              label="Deactivated"
              value={metrics?.deactivatedUsers}
              accent="muted"
              loading={isLoading}
              error={hasError}
              to="/users"
            />
          </div>
        </section>

        {/* Alerts */}
        <section>
          <SectionLabel>Alerts</SectionLabel>
          <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <StatCard
              label="Pending Requests"
              value={metrics?.pendingRequests}
              accent={
                metrics && metrics.pendingRequests > 0 ? "amber" : "green"
              }
              loading={isLoading}
              error={hasError}
              to="/requests"
              hint={
                metrics
                  ? metrics.pendingRequests > 0
                    ? { text: "Needs review", tone: "warn" }
                    : { text: "All clear", tone: "good" }
                  : undefined
              }
            />
            <StatCard
              label="No-Admin Companies"
              value={metrics?.noAdminCompanies}
              accent={
                metrics && metrics.noAdminCompanies > 0 ? "red" : "green"
              }
              loading={isLoading}
              error={hasError}
              to="/companies"
              hint={
                metrics
                  ? metrics.noAdminCompanies > 0
                    ? { text: "Action required", tone: "bad" }
                    : { text: "All clear", tone: "good" }
                  : undefined
              }
            />
          </div>
        </section>

        {/* Footer */}
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span>
            {hasError
              ? "Failed to load metrics."
              : lastUpdated
              ? `Last updated: ${lastUpdated.toLocaleTimeString()}`
              : "Loading…"}
          </span>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center justify-center h-6 w-6 rounded hover:bg-slate-800 text-slate-400 hover:text-slate-200 transition-colors disabled:opacity-50"
            aria-label="Refresh metrics"
            title="Refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
